import { useEffect, useId, useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  rectIntersection,
  type CollisionDetection,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  cn,
  comptesParNiveau,
  libellesNiveauxQsort,
  melangerDeterministe,
  type PlacementsQsort,
} from "@repo/utils";
import { Check } from "lucide-react";
import { useIsMobile } from "./use-is-mobile";
import { useQsortPlacement } from "./use-qsort-placement";
import { QsortFeuilleMobile, QsortNiveauxMobile } from "./qsort-feuille-mobile";
import {
  anneauFocus,
  ChipDraggable,
  ChipEnVol,
  ColonneNiveau,
  ID_RESERVE,
  prefixeItem,
  Zone,
  type QsortItem,
} from "./qsort-board-elements";

export type { QsortItem };

type Props = {
  items: QsortItem[];
  /** Capacité de chaque niveau. Niveau 0 = le plus fort. */
  pyramide: number[];
  placements: PlacementsQsort;
  onChange: (placements: PlacementsQsort) => void;
  /** Libellés des niveaux. Par défaut : sémantiques (décision §11.1). */
  libellesNiveaux?: string[];
  lectureSeule?: boolean;
  /**
   * Graine du mélange des items (id d'évaluation, de notation…). Sans graine,
   * l'ordre reçu est conservé — ce qui, pour le socle fit-culturel, revient à
   * présenter les items groupés par dimension.
   */
  graineOrdre?: string;
};

/**
 * Dépôt sous le POINTEUR, pas là où l'aperçu recouvre le plus : l'aperçu d'une
 * chip de réserve est deux fois plus large qu'une colonne, et `rectIntersection`
 * (le défaut) déposait dans la colonne voisine. Sous le pointeur, un item passe
 * avant sa colonne — c'est lui qu'on désigne pour l'échange.
 */
const collisionSousPointeur: CollisionDetection = (args) => {
  const sous = pointerWithin(args);
  if (sous.length === 0) return rectIntersection(args);
  const item = sous.find((c) => String(c.id).startsWith(prefixeItem));
  return item ? [item] : sous;
};

const annoncesDnd = {
  onDragStart: () => undefined,
  onDragOver: () => undefined,
  onDragEnd: () => undefined,
  onDragCancel: () => undefined,
};

/**
 * Tableau Q-sort à distribution forcée — écran commun au référent entreprise et
 * au candidat (plan §7.1). Composant CONTRÔLÉ : l'autosave et la soumission
 * vivent chez l'appelant, qui seul sait à quelle mutation parler.
 *
 * La pyramide est au-dessus de la réserve et ses emplacements sont dessinés :
 * sa hauteur ne dépend pas du remplissage, donc la cible ne se déplace pas
 * pendant qu'on vide la réserve.
 *
 * Deux modes de placement, toujours actifs tous les deux :
 * - **tap** — sélectionner un item, puis cliquer un niveau. C'est le chemin
 *   principal sur mobile (la pyramide horizontale ne tient pas, on passe en
 *   accordéon vertical) et il sert de repli accessible au clavier sur desktop.
 * - **drag & drop** — desktop uniquement : au tactile le geste entre en conflit
 *   avec le scroll de page, comme constaté sur le PAMI.
 *
 * Un dépôt sur un niveau PLEIN échange les deux items au lieu d'être refusé
 * (`placer`), ce qui garde l'état toujours valide vis-à-vis des quotas.
 */
export function QsortBoard({
  items: itemsRecus,
  pyramide,
  placements,
  onChange,
  libellesNiveaux,
  lectureSeule = false,
  graineOrdre,
}: Props) {
  const items = useMemo(
    () => (graineOrdre ? melangerDeterministe(itemsRecus, graineOrdre) : itemsRecus),
    [itemsRecus, graineOrdre],
  );
  const isMobile = useIsMobile();
  const dnd = !isMobile && !lectureSeule;
  const [enVol, setEnVol] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const libelles = useMemo(
    () => libellesNiveaux ?? libellesNiveauxQsort(pyramide.length),
    [libellesNiveaux, pyramide.length],
  );
  const parCode = useMemo(
    () => new Map(items.map((item) => [item.code, item])),
    [items],
  );
  const comptes = comptesParNiveau(placements, pyramide.length);
  const {
    selection,
    niveauPlein,
    abandonnerSelection,
    quitterRemplacement,
    echange,
    consigne,
    tapItem,
    tapNiveau,
    deposer,
    annulerEchange,
    varianteDe,
  } = useQsortPlacement({
    parCode,
    pyramide,
    placements,
    onChange,
    libelles,
    lectureSeule,
    isMobile,
  });
  const reserve = items.filter((item) => placements[item.code] == null);
  const nbClasses = items.length - reserve.length;
  // Le moment où tout est placé mérite d'être dit : sans ça, rien ne marquait
  // la fin du tri, sinon un bouton qui changeait de couleur hors de l'écran.
  const complet = items.length > 0 && reserve.length === 0;
  const itemsDuNiveau = (niveau: number) =>
    items.filter((item) => placements[item.code] === niveau);
  const selectionPlacee = selection !== null && placements[selection] != null;

  const onDragStart = (e: DragStartEvent) =>
    setEnVol(String(e.active.id).slice(prefixeItem.length));

  const onDragEnd = (e: DragEndEvent) => {
    setEnVol(null);
    if (!e.over) return;
    deposer(String(e.active.id).slice(prefixeItem.length), String(e.over.id));
  };

  const idReserve = useId();

  // Échap abandonne la sélection en cours — le geste attendu pour « je me suis
  // trompé d'item », au clavier comme dans la feuille mobile.
  useEffect(() => {
    if (selection === null) return;
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === "Escape") abandonnerSelection();
    };
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, [selection, abandonnerSelection]);

  /**
   * Placement déclenché depuis un niveau. Au clavier, le focus part ensuite sur
   * le prochain item à classer : sinon il restait sur l'en-tête du niveau, et il
   * fallait retraverser toute la pyramide pour atteindre la réserve.
   */
  const placerAuNiveau = (niveau: number | null, parClavier: boolean) => {
    const place = tapNiveau(niveau);
    if (!place || !parClavier) return;
    requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>(
          `[aria-labelledby="${CSS.escape(idReserve)}"] button[aria-pressed]`,
        )
        ?.focus();
    });
  };

  const pyramideDesktop = (
    // Les colonnes rétrécissent avec l'écran (minmax 0) ; le scroll horizontal
    // ne reste qu'un filet sous ~700 px de contenu, où les libellés ne tiennent plus.
    <section aria-label="Votre classement" className="overflow-x-auto">
      <h2 className="sr-only">Votre classement</h2>
      <div
        className="grid min-w-[42rem] items-start gap-1.5"
        style={{ gridTemplateColumns: `repeat(${pyramide.length}, minmax(0, 1fr))` }}
      >
        {pyramide.map((capacite, niveau) => (
          <ColonneNiveau
            key={niveau}
            niveau={niveau}
            libelle={libelles[niveau] ?? `Niveau ${niveau + 1}`}
            capacite={capacite}
            intensite={pyramide.length > 1 ? 1 - niveau / (pyramide.length - 1) : 1}
            items={itemsDuNiveau(niveau)}
            selection={selection}
            libelleSelection={selection !== null ? parCode.get(selection)?.label : undefined}
            varianteDe={varianteDe}
            lectureSeule={lectureSeule}
            dnd={dnd}
            onTapNiveau={(parClavier) => placerAuNiveau(niveau, parClavier)}
            onTapItem={tapItem}
          />
        ))}
      </div>
    </section>
  );

  const niveauxMobile = (
    <QsortNiveauxMobile
      pyramide={pyramide}
      libelles={libelles}
      comptes={comptes}
      itemsDuNiveau={itemsDuNiveau}
      selection={selection}
      varianteDe={varianteDe}
      lectureSeule={lectureSeule}
      onTapNiveau={placerAuNiveau}
      onTapItem={tapItem}
    />
  );

  // Sur mobile, l'annonce d'un échange part en bas d'écran : en haut du
  // tableau, elle tomberait hors de vue de qui vient de placer depuis la feuille.
  const annonceEchangeMobile =
    isMobile && !lectureSeule && echange && selection === null ? (
      <div
        role="status"
        className="fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-40 flex items-center justify-between gap-3 rounded-lg border border-charte-line-accent bg-charte-hover-opaque py-1 pr-1 pl-3 text-sm shadow-lg"
      >
        <span>{echange.texte}</span>
        <button
          type="button"
          onClick={annulerEchange}
          className="min-h-11 shrink-0 rounded-lg px-3 font-medium text-charte-accent-light underline underline-offset-4"
        >
          Annuler
        </button>
      </div>
    ) : null;

  const itemSelectionne = selection !== null ? parCode.get(selection) : undefined;
  const feuilleMobile =
    isMobile && !lectureSeule && itemSelectionne ? (
      <QsortFeuilleMobile
        item={itemSelectionne}
        niveauActuel={placements[itemSelectionne.code] ?? null}
        libelles={libelles}
        pyramide={pyramide}
        comptes={comptes}
        niveauPlein={niveauPlein}
        occupantsNiveauPlein={niveauPlein === null ? [] : itemsDuNiveau(niveauPlein)}
        onNiveau={placerAuNiveau}
        onRemplacer={tapItem}
        onQuitterRemplacement={quitterRemplacement}
        onFermer={abandonnerSelection}
      />
    ) : null;

  const zoneReserve = (
    <Zone
      id={ID_RESERVE}
      role="region"
      ariaLabelledby={idReserve}
      onClick={selectionPlacee ? () => tapNiveau(null) : undefined}
      className={cn(
        "-mx-2 space-y-3 rounded-lg px-2 py-2 transition-colors",
        selectionPlacee && "cursor-pointer hover:bg-charte-hover",
      )}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 id={idReserve} className="text-sm font-semibold">
          À classer ({reserve.length})
        </h2>
        {selectionPlacee && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              tapNiveau(null);
            }}
            className="text-sm font-medium text-charte-accent-light underline-offset-4 hover:underline"
          >
            Remettre dans « À classer »
          </button>
        )}
      </div>
      {reserve.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Tous les items sont classés.
        </p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {reserve.map((item) => (
            <ChipDraggable
              key={item.code}
              item={item}
              actif={selection === item.code}
              variante={varianteDe(item.code)}
              lectureSeule={lectureSeule}
              dnd={dnd}
              poignee
              onTap={() => tapItem(item.code)}
            />
          ))}
        </div>
      )}
    </Zone>
  );

  const contenu = (
    <div className="space-y-5">
      {!lectureSeule && (
        // Une seule ligne d'état, au-dessus de la pyramide : un bandeau de plus
        // à l'échange ferait descendre la cible au moment même où l'on vise.
        <div className="flex min-h-7 flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
          {complet ? (
            <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-charte-accent-light">
              <Check className="size-4" aria-hidden />
              Classement complet
            </p>
          ) : (
            <p className="text-sm font-medium tabular-nums">
              {nbClasses} sur {items.length} classés
            </p>
          )}
          <div aria-live="polite" className="text-sm">
            {echange && selection === null && !isMobile ? (
              <p className="flex flex-wrap items-baseline justify-end gap-x-3 gap-y-1">
                <span className="text-charte-accent-light">{echange.texte}</span>
                <button
                  type="button"
                  onClick={annulerEchange}
                  className={cn(
                    "rounded-sm font-medium text-charte-accent-light underline underline-offset-4 hover:no-underline",
                    anneauFocus,
                  )}
                >
                  Annuler l'échange
                </button>
              </p>
            ) : (
              <p
                className={cn(
                  selection !== null
                    ? "font-medium text-charte-accent-light"
                    : "text-muted-foreground",
                )}
              >
                {complet && selection === null
                  ? "Relisez votre pyramide : vous pouvez encore déplacer des items avant de valider."
                  : consigne}
              </p>
            )}
          </div>
        </div>
      )}

      {isMobile ? (
        <>
          {zoneReserve}
          {niveauxMobile}
          {feuilleMobile}
          {annonceEchangeMobile}
          {/* La feuille recouvre le bas de page : cette marge garde les
              derniers niveaux atteignables pendant qu'elle est ouverte. */}
          {(feuilleMobile || annonceEchangeMobile) && (
            <div aria-hidden className={feuilleMobile ? "h-72" : "h-20"} />
          )}
        </>
      ) : (
        <>
          {pyramideDesktop}
          <div className="border-t border-charte-line pt-4">{zoneReserve}</div>
        </>
      )}
    </div>
  );

  if (!dnd) return contenu;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionSousPointeur}
      // Le glisser est réservé à la souris : ses annonces anglaises par défaut
      // (« Picked up draggable item… ») n'apportaient rien, et le résultat du
      // dépôt est déjà annoncé en français par la ligne d'état.
      accessibility={{
        announcements: annoncesDnd,
        screenReaderInstructions: { draggable: "" },
      }}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setEnVol(null)}
    >
      {contenu}
      <DragOverlay>
        {enVol && parCode.get(enVol) ? <ChipEnVol item={parCode.get(enVol)!} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
